import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, NavigationStart, Event, NavigationError, NavigationCancel } from '@angular/router';
import { OktaAuthService } from '@okta/okta-angular';
import { UserIdleService } from 'angular-user-idle';
import { FclLoginService } from './services/fcllogin.service';
import { RoutingState } from './services/routing-state.service';
import { Observable, Subscription, throwError, timer } from 'rxjs';
import { concatMap, catchError, filter } from 'rxjs/operators';
import { OktaAuth } from '@okta/okta-auth-js';
import { environment } from 'src/environments/environment';
import { User } from './model/user';
import { TranslateService } from '@ngx-translate/core';
import { UserActivityService } from './services/user-activity.service';

export let browserRefresh = false;

// Global Token Management Configuration
const TOKEN_CONFIG = {
  // Set to true for testing (1-minute cycles), false for production
  TESTING_MODE: false,
  
  // Production settings (when TESTING_MODE = false)
  PRODUCTION: {
    INITIAL_DELAY_MINUTES: 1,           // Initial refresh delay after login
    REFRESH_BEFORE_EXPIRY_MINUTES: 5,   // Refresh 5 minutes before expiry
    MIN_REFRESH_INTERVAL_MINUTES: 1,    // Minimum refresh interval
    RETRY_DELAY_MINUTES: 5,             // Retry delay on failure
    EXPIRY_THRESHOLD_MINUTES: 10        // Refresh if expires within 10 minutes
  },
  
  // Testing settings (when TESTING_MODE = true)
  TESTING: {
    INITIAL_DELAY_MINUTES: 1,           // Initial refresh delay after login
    REFRESH_INTERVAL_MINUTES: 1,        // Refresh every 1 minute
    RETRY_DELAY_MINUTES: 1,             // Retry after 1 minute on failure
    ALWAYS_REFRESH: true                // Always refresh regardless of expiry
  }
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy {

  title = 'Fsc Customer Portal';
  togglParent = true;
  public isAuthenticated: boolean;
  userSessionExtendPopup = false;
  timerStartSubscription: Subscription;
  timeOutSubscription: Subscription;
  currentRoute = '/';
  subscription: Subscription;
  timeCounter: number = 3000;
  timeLeft: number = this.timeCounter;
  interval: any;
  subscribeTimer: any;
  sessionId = '';
  config = {
    clientId: environment.oktaClientId,
    issuer: environment.oktaIssuer,
    redirectUri: environment.oktaURL + '/authorization-code/callback',
    scopes: environment.oktaScopes,
    pkce: true
  }
  authClient = new OktaAuth(this.config);
  intervalId: any;
  private navigationStart: number;
  private isRefreshingToken: boolean = false;

  // Helper function to format date as yyyy-MM-dd hh:mm:ss
  private formatDateTime(date: Date = new Date()): string {
    const pad = (num: number) => num.toString().padStart(2, '0');
    
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  constructor(
    public oktaAuth: OktaAuthService,
    routingState: RoutingState,
    private userIdle: UserIdleService,
    private fclLoginService: FclLoginService,
    public router: Router,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
	private userActivityService: UserActivityService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.oktaAuth.$authenticationState.subscribe(isAuthenticated => this.isAuthenticated = isAuthenticated);
    routingState.loadRouting();
    this.subscription = router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        browserRefresh = !router.navigated;
      }
    });
  }

  async ngOnInit() {
    await this.router.events.subscribe(
      (event: any) => {
        if (event instanceof NavigationEnd) {
          this.currentRoute = this.router.url;
        }
      }
    );

    // Handle lazy-load chunk errors caused by stale browser cache after deployments.
    // When a ChunkLoadError occurs, reload the page once to fetch the latest chunks.
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationError) {
        const isChunkError =
          event.error && (
            event.error.name === 'ChunkLoadError' ||
            /Loading chunk [\d]+ failed/i.test(event.error.message) ||
            /Loading CSS chunk [\d]+ failed/i.test(event.error.message)
          );
        if (isChunkError) {
          const reloadKey = 'chunk_error_reload';
          if (!sessionStorage.getItem(reloadKey)) {
            sessionStorage.setItem(reloadKey, '1');
            window.location.assign(event.url);
          }
        } else {
          sessionStorage.removeItem('chunk_error_reload');
        }
      } else if (event instanceof NavigationEnd) {
        sessionStorage.removeItem('chunk_error_reload');
      }
    });
	this.router.events.subscribe(event => {
	  if (event instanceof NavigationStart) {
	    this.navigationStart = performance.now();
	  } else if (event instanceof NavigationEnd) {
	    const duration = performance.now() - this.navigationStart;
	    this.userActivityService.logEvent('navigation', { url: event.urlAfterRedirects, duration });
	  }
	});
    this.oktaAuth.$authenticationState.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        console.log('🚀 User authenticated at:', this.formatDateTime());
        
        // Initial token refresh after configured delay
        const initialDelay = TOKEN_CONFIG.TESTING_MODE 
          ? TOKEN_CONFIG.TESTING.INITIAL_DELAY_MINUTES 
          : TOKEN_CONFIG.PRODUCTION.INITIAL_DELAY_MINUTES;
          
        setTimeout(() => {
          console.log('Initial token refresh after app startup');
          this.getOKTARefreshToken(true);
        }, initialDelay * 60 * 1000);
      } else {
        console.log('🔒 User logged out at:', this.formatDateTime());
        // Handle unauthenticated state
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
      }
    });
    this.userIdle.startWatching();
    const timeout = this.userIdle.getConfigValue().timeout;

    // Start watching when user idle is starting.
    this.timerStartSubscription = this.userIdle.onTimerStart().subscribe(count => {
      // Log when user idle starts (first count)
      if (count === timeout) {
        console.log('😴 USER IDLE STARTED at:', this.formatDateTime(), '- Countdown begins');
      }
      // this.userSessionExtendPopup = true;
      if (this.currentRoute !== '/') {
        if (count !== null && count !== undefined && !this.userSessionExtendPopup) {
          const closePopupTime: number = +timeout - 2;
          this.userSessionExtendPopup = (+count <= closePopupTime);
          this.changeDetectorRef.markForCheck();
        }
      }
    });

    // Start watch when time is up.
    this.timeOutSubscription = this.userIdle.onTimeout().subscribe(() => {
      const timeoutTime = this.formatDateTime();
      console.log('⏰ USER IDLE TIMEOUT at:', timeoutTime);
      console.log('🔴 Session expired due to inactivity - logging out user');
      
      this.userSessionExtendPopup = false;
      if (this.currentRoute !== '/') {

        if (this.fclLoginService.getUserType() === 'I' || this.fclLoginService.getUserType() === 'T') {
          console.log('🔒 Logging out internal user due to idle timeout at:', timeoutTime);
          this.fclLoginService.user = undefined;
          this.fclLoginService.clearLocalStorage();
          setTimeout(() => {
            this.authClient.signOut();
            this.oktaAuth.logout('/');
            this.changeDetectorRef.markForCheck();
          }, 200);
        } else if (this.fclLoginService.getUserType() === 'E') {
          console.log('🔒 Logging out external user due to idle timeout at:', timeoutTime);
          this.fclLoginService.user = undefined;
          this.fclLoginService.isUserLoggedIn = false;
          this.fclLoginService.clearLocalStorage();
          setTimeout(() => {
            this.router.navigate(['/']);
            this.onClose();
          }, 200);
        }
      }
    });

    this.userIdle.ping$.subscribe(() => {
      console.log('👆 User activity detected - ping event at:', this.formatDateTime());
      if (this.currentRoute !== '/') {
        if (this.fclLoginService.getUserType() === 'I' || this.fclLoginService.getUserType() === 'T') {
          console.log('🔍 Checking OKTA session status for internal user...');
          // For internal users, ensure OKTA session is refreshed on activity
          this.authClient.session.exists().then(exists => {
            if (!exists) {
              console.log('❌ OKTA session expired, refreshing...');
              this.getOKTARefreshToken();
            } else {
              console.log('✅ OKTA session still active');
            }
          }).catch(error => {
            console.error('❌ Error checking OKTA session:', error);
          });
        } else if (this.fclLoginService.getUserType() === 'E') {
          console.log('🔄 Extending external user session...');
          const fclAccessToken$ = this.fclLoginService.fclAccessToken();
          fclAccessToken$.pipe(
            concatMap((fclTokenResponse) => {
              const accessToken = fclTokenResponse.access_token;
              return this.fclLoginService.extendfclSession(accessToken);
            }),
            catchError(err => {
              console.error('❌ External user session extension failed:', err);
              return throwError(err);
            })
          ).subscribe(extendfclSessionResponse => {
            console.log('✅ External user session extended successfully at:', this.formatDateTime());
          }, (error) => {
            console.error('❌ Failed to extend external user session:', error);
          });
        }
      }
    });
  }

  getOKTARefreshToken(isInitial?:boolean) {
    try {
      // Prevent concurrent refresh attempts
      if (this.isRefreshingToken) {
        console.log('🔄 Token refresh already in progress, skipping duplicate attempt at:', this.formatDateTime());
        return;
      }

      if (this.authClient.isAuthenticated()) {
        console.log('🔄 Running Refresh Token Method at:', this.formatDateTime());
        
        // Check if token is close to expiry before refreshing
        this.authClient.tokenManager.get('accessToken').then(token => {
          if (token && token.expiresAt) {
            const now = Date.now() / 1000;
            const timeUntilExpiry = token.expiresAt - now;
            const expiryTime = this.formatDateTime(new Date(token.expiresAt * 1000));
            
            // console.log('📊 Token Analysis:');
            // console.log('   - Current time:', this.formatDateTime());
            // console.log('   - Token expires at:', expiryTime);
            // console.log('   - Time until expiry:', Math.floor(timeUntilExpiry / 60), 'minutes', Math.floor(timeUntilExpiry % 60), 'seconds');
            
            // Check if token needs refresh based on configuration
            const shouldRefresh = TOKEN_CONFIG.TESTING_MODE 
              ? TOKEN_CONFIG.TESTING.ALWAYS_REFRESH 
              : timeUntilExpiry < (TOKEN_CONFIG.PRODUCTION.EXPIRY_THRESHOLD_MINUTES * 60);
              
            if (shouldRefresh) {
              const reason = TOKEN_CONFIG.TESTING_MODE 
                ? 'testing mode (configured refresh cycle)' 
                : 'token expires soon';
              console.log(`⚠️ Token refresh triggered - ${reason}`);
              
              if (timeUntilExpiry <= 0) {
                console.log('🔴 TOKEN EXPIRED at:', this.formatDateTime());
              }
              return this.performTokenRefresh();
            } else {
              if(isInitial){
                console.log(`⚠️ Token refresh triggered - Initial refresh`);
                return this.performTokenRefresh();
              }
              console.log('✅ Token still valid, skipping refresh. Expires in:', Math.floor(timeUntilExpiry / 60), 'minutes');
              this.scheduleNextTokenRefresh(timeUntilExpiry);
            }
          } else {
            console.log('❌ No valid token found, performing refresh...');
            return this.performTokenRefresh();
          }
        }).catch(error => {
          console.error('❌ Error checking token expiry:', error);
          this.performTokenRefresh();
        });
      } else {
        console.log('🔒 User not authenticated, skipping token refresh');
      }
    } catch (error) {
      console.error('❌ Error in getOKTARefreshToken:', error);
    }
  }

  private scheduleNextTokenRefresh(timeUntilExpiry: number) {
    // Clear any existing interval
    if (this.intervalId) {
      console.log('🧹 Clearing existing scheduled refresh before setting new one');
      clearTimeout(this.intervalId);
      this.intervalId = null;
    }
    
    let refreshIn: number;
    let logMessage: string;
    
    if (TOKEN_CONFIG.TESTING_MODE) {
      // Testing mode: refresh every configured interval
      refreshIn = TOKEN_CONFIG.TESTING.REFRESH_INTERVAL_MINUTES * 60 * 1000;
      logMessage = `TESTING - ${TOKEN_CONFIG.TESTING.REFRESH_INTERVAL_MINUTES} minute cycle`;
    } else {
      // Production mode: refresh before expiry with minimum interval
      const beforeExpiryMs = Math.max(
        (timeUntilExpiry - (TOKEN_CONFIG.PRODUCTION.REFRESH_BEFORE_EXPIRY_MINUTES * 60)) * 1000, 
        TOKEN_CONFIG.PRODUCTION.MIN_REFRESH_INTERVAL_MINUTES * 60 * 1000
      );
      refreshIn = beforeExpiryMs;
      logMessage = `PRODUCTION - ${TOKEN_CONFIG.PRODUCTION.REFRESH_BEFORE_EXPIRY_MINUTES} minutes before expiry`;
    }

    const nextRefreshTime = this.formatDateTime(new Date(Date.now() + refreshIn));
    
    console.log(`📅 Scheduling next token refresh (${logMessage}):`);
    console.log('   - Refresh in:', Math.floor(refreshIn / 60000), 'minutes');
    console.log('   - Next refresh at:', nextRefreshTime);
    if (!TOKEN_CONFIG.TESTING_MODE) {
      console.log('   - Token expires in:', Math.floor(timeUntilExpiry / 60), 'minutes');
    }
    
    this.intervalId = setTimeout(() => {
      console.log('⏰ Scheduled token refresh triggered at:', this.formatDateTime());
      this.getOKTARefreshToken();
    }, refreshIn);
    
    console.log('✅ Timer ID set:', this.intervalId ? 'Active' : 'Failed');
  }

  private performTokenRefresh() {
    // Set the refresh flag
    this.isRefreshingToken = true;
    const refreshStartTime = this.formatDateTime();
    console.log('🚀 Token refresh started at:', refreshStartTime);
    
    this.authClient.session.refresh().then(() => {
      return this.authClient.token.getWithoutPrompt();
    }).then(token => {
      const refreshCompleteTime = this.formatDateTime();
      console.log('🎉 NEW TOKEN GENERATED at:', refreshCompleteTime);
      
      this.authClient.tokenManager.setTokens(token.tokens);
      let user: User = JSON.parse(localStorage.getItem('USER'));
      if (user && token.tokens.accessToken) {
        var tokens = token.tokens.accessToken;
        const newExpiryTime = this.formatDateTime(new Date(tokens.expiresAt * 1000));
        
        console.log('📋 New Token Details:');
        console.log('   - Generated at:', refreshCompleteTime);
        console.log('   - Expires at:', newExpiryTime);
        console.log('   - Duration:', Math.floor((tokens.expiresAt * 1000 - Date.now()) / 60000), 'minutes');
        
        user.jwtToken = tokens.accessToken;
        localStorage.removeItem('USER');
        
        this.fclLoginService.getGatewayAccessToken().subscribe(async x => {
          await x;
          localStorage.removeItem('gateway-access-token');
          localStorage.setItem('gateway-access-token', x.access_token);
        });
        
        this.fclLoginService.populateUserInfo(user);
        
        // Schedule next refresh based on the new token's expiry time
        if (tokens.expiresAt) {
          const now = Date.now() / 1000;
          const timeUntilExpiry = tokens.expiresAt - now;
          this.scheduleNextTokenRefresh(timeUntilExpiry);
        }
      }
      this.timeLeft = this.timeCounter;
      
      // Clear the refresh flag on success
      this.isRefreshingToken = false;
    }).catch(error => {
      // Clear the refresh flag on error
      this.isRefreshingToken = false;
      
      const errorTime = this.formatDateTime();
      console.error('❌ Token refresh failed at:', errorTime);
      console.error('❌ Error details:', error);
      
      // Clear any existing scheduled refresh to prevent overlapping attempts
      if (this.intervalId) {
        clearTimeout(this.intervalId);
        this.intervalId = null;
        console.log('🧹 Cleared existing scheduled refresh due to error');
      }
      
      // Schedule a retry based on configuration
      const retryDelay = TOKEN_CONFIG.TESTING_MODE 
        ? TOKEN_CONFIG.TESTING.RETRY_DELAY_MINUTES 
        : TOKEN_CONFIG.PRODUCTION.RETRY_DELAY_MINUTES;
        
      const retryTime = this.formatDateTime(new Date(Date.now() + (retryDelay * 60 * 1000)));
      console.log(`⏳ Scheduling retry in ${retryDelay} minute(s) at: ${retryTime}`);
      
      this.intervalId = setTimeout(() => {
        console.log('🔄 Retrying token refresh after failure at:', this.formatDateTime());
        this.getOKTARefreshToken();
      }, retryDelay * 60 * 1000);
    });
  }

  toggleMenu(event) {
    this.togglParent = event;
  }

  onClose() {
    this.userIdle.resetTimer();
    this.userIdle.startWatching();
    this.userSessionExtendPopup = false;
  }

  ngOnDestroy() {
    if (this.intervalId) {
      clearTimeout(this.intervalId);
    }
    if (this.timerStartSubscription) {
      this.timerStartSubscription.unsubscribe();
    }
    if (this.timeOutSubscription) {
      this.timeOutSubscription.unsubscribe();
    }
    this.subscription.unsubscribe();
  }

  returnCookie() {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const cookieName = cookie.split('=')[0];
      if (cookieName !== 'okta-oauth-nonce' && cookieName !== 'okta-oauth-state') {
        document.cookie = cookieName + "=;domain=.fedex.com;path=/;max-age=0";
      }
    }
    return document.cookie;
  }
}
